import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
  type QueryKey,
} from "@tanstack/react-query"
import type { AxiosRequestConfig } from "axios"
import { apiClient } from "@/lib/api-client"
import { toast } from "@/hooks/use-toast"

// Type-safe query hook
export function useApiQuery<TData>(
  queryKey: QueryKey,
  config: AxiosRequestConfig,
  options?: Omit<UseQueryOptions<TData, Error, TData, QueryKey>, "queryKey" | "queryFn">,
) {
  return useQuery<TData, Error>({
    queryKey,
    queryFn: () => apiRequest<TData>(config),
    ...options,
  })
}

// Type-safe mutation hook
export function useApiMutation<TData, TVariables>(
  config: (variables: TVariables) => AxiosRequestConfig,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, "mutationFn">,
) {
  const queryClient = useQueryClient()

  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) => apiRequest<TData>(config(variables)),
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "An error occurred",
        variant: "destructive",
      })
    },
    ...options,
  })
}

// Optimistic update helper
export function optimisticUpdate<TData, TVariables>({
  queryKey,
  updateFn,
  rollbackFn,
}: {
  queryKey: QueryKey
  updateFn: (oldData: TData | undefined, variables: TVariables) => TData
  rollbackFn?: (oldData: TData | undefined, variables: TVariables) => TData
}) {
  const queryClient = useQueryClient()

  return {
    onMutate: async (variables: TVariables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<TData>(queryKey)

      // Optimistically update to the new value
      queryClient.setQueryData<TData>(queryKey, (old) => updateFn(old, variables))

      // Return a context object with the snapshot
      return { previousData, variables }
    },
    onError: (err: Error, variables: TVariables, context: any) => {
      if (rollbackFn && context.previousData) {
        queryClient.setQueryData<TData>(queryKey, rollbackFn(context.previousData, variables))
      } else {
        // Rollback to the previous value
        queryClient.setQueryData(queryKey, context.previousData)
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey })
    },
  }
}

// Helper function to extract error message from complex error response
function extractErrorMessage(errorData: any): string {
  try {
    // Check for the specific structure you're receiving
    if (errorData?.message?.message) {
      return errorData.message.message
    }

    // Check for direct message
    if (errorData?.message && typeof errorData.message === "string") {
      return errorData.message
    }

    // Check for error array (validation errors)
    if (Array.isArray(errorData?.message)) {
      return errorData.message[0]
    }

    // Check for axios error response
    if (errorData?.response?.data) {
      return extractErrorMessage(errorData.response.data)
    }

    // Check if it's an Error object
    if (errorData instanceof Error) {
      return errorData.message
    }

    // Fallback to generic message
    return "An unexpected error occurred"
  } catch (e) {
    console.error("Error while extracting error message:", e)
    return "An unexpected error occurred"
  }
}

// Function to show toast with deduplication
function showErrorToast(message: string) {
  // Only run on client side
  if (typeof window === "undefined") return

  // Use setTimeout to ensure the toast is shown after the current execution context
  setTimeout(() => {
    toast({
      title: "Error",
      description: message,
      variant: "destructive",
    })
  }, 0)
}

// Type-safe API request function that uses the apiClient from lib/api-client
export async function apiRequest<T = any>(config: AxiosRequestConfig): Promise<T> {
  try {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data || "")
    const response = await apiClient.request<T>(config)
    console.log(`API Response: ${response.status} ${config.url}`)
    return response.data
  } catch (error: any) {
    console.error("API Error:", config.url, error)
    throw error
  }
}

// Hook to provide API client instance
export function useApi() {
  return {
    api: apiClient,
    request: apiRequest,
  }
}
